import {
    clearScenesPendingAgent,
    enterCreationStage,
    markSceneBlurred,
    markScenesPendingAgent,
    markSceneTouchedByAgent,
} from './agent-activity-actions';
import { getCreationStage } from './agent-activity-selectors';
import { CreationStage } from './agent-activity-types';

import {
    getAnalyticsSessionId,
    getStageDurationProperties,
    recordStageStart,
    resetStageTimings,
} from 'js/src/libs/analytics-session';

import type { ToolExecutionContext } from 'js/src/agent/types';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';

const CREATION_STAGE_EVENT_TITLE: Record<CreationStage, string> = {
    [CreationStage.PLANNING]: 'Planning',
    [CreationStage.BUILDING]: 'Building',
    [CreationStage.REVIEWING]: 'Reviewing',
};

/** Hold scenes under the agent-activity indicator, blurred, for the rest of the turn. */
export function markScenesBusy(dispatch: AppDispatch, sceneIds: string[]): void {
    if (sceneIds.length === 0) {
        return;
    }
    dispatch(markSceneBlurred({ sceneIds }));
    dispatch(markSceneTouchedByAgent({ sceneIds }));
}

export function markScenesPending(dispatch: AppDispatch, sceneIds: string[]): void {
    if (sceneIds.length === 0) {
        return;
    }
    dispatch(markScenesPendingAgent({ sceneIds }));
}

export function clearScenesPending(dispatch: AppDispatch, sceneIds: string[]): void {
    if (sceneIds.length === 0) {
        return;
    }
    dispatch(clearScenesPendingAgent({ sceneIds }));
}

/**
 * Advances the from-scratch build to `stage` and fires a Mixpanel event the first
 * time the visible stage actually changes. enterCreationStage is monotonic and
 * de-dupes repeat/out-of-order announcements — several tools all announce
 * `reviewing`, for instance — so this compares state before and after the dispatch
 * rather than tracking unconditionally, which would double-fire per tool call.
 *
 * Each event also carries the page-load session id and how long each preceding stage
 * took, so a single build can be followed end to end in Mixpanel.
 */
export function announceCreationStage(
    dispatch: AppDispatch,
    getState: GetRootState,
    context: Pick<ToolExecutionContext, 'subagentDescription'>,
    stage: CreationStage,
    eventProperties: Record<string, unknown> = {},
): void {
    // The build belongs to the agent that started it. A subagent builds and reviews only its
    // own part while its siblings may still be building theirs, so it never moves the stage.
    if (context.subagentDescription !== undefined) {
        return;
    }
    const before = getCreationStage(getState());
    dispatch(enterCreationStage({ stage }));
    const after = getCreationStage(getState());
    if (after !== null && after !== before) {
        // Planning out of null is the only way into a build. Timings are cleared here
        // rather than on clearAllAgentActivity because the interactable event reads them
        // after that has already run.
        if (before === null) {
            resetStageTimings();
        }
        // Recorded before reading the durations so that entering this stage closes the
        // previous one's bracket and it can report its elapsed time.
        recordStageStart(after);
        analytics.track(`Agent: Started ${CREATION_STAGE_EVENT_TITLE[after]} Stage`, {
            ...eventProperties,
            session_id: getAnalyticsSessionId(),
            ...getStageDurationProperties(),
        });
    }
}
